<section aria-labelledby="noc-heading">
  <h2 id="noc-heading">Create <span class="emphasis">NOC</span> Ticket</h2>
  @if (nocFormGroup) {
    <form
      [formGroup]="nocFormGroup"
      [ngClass]="{ 'form-submit-attempt': formSubmitAttempted() }"
      autocomplete="off"
    >
      <app-primary-reporter
        [formRef]="nocFormGroup"
        [reporters]="primaryReporters"
        (usersLoaded)="usersLoaded()"
      >
      </app-primary-reporter>

      <app-select-single-service
        [formRef]="nocFormGroup"
        [services]="services"
        [selectedServiceId]="selectedServiceId"
        [inlineErrors]="inlineErrors"
        (servicesLoaded)="servicesLoaded()"
      >
      </app-select-single-service>

      <div class="mt-4">
        <label id="serviceAreaLabel" class="comcast-input-label">
          What service area do you need support with?
          <span class="comcast-required-marker">*</span>
        </label>
        <div class="relative mt-2">
          <button
            type="button"
            class="comcast-select-trigger"
            [class.comcast-select-trigger--open]="isServiceAreaDropdownOpen()"
            [class.comcast-select-trigger--filled]="!!selectedServiceAreaLabel"
            [class.comcast-input-invalid]="inlineErrors.serviceArea"
            [attr.aria-expanded]="isServiceAreaDropdownOpen()"
            [attr.aria-invalid]="!!inlineErrors.serviceArea || null"
            [attr.aria-describedby]="
              inlineErrors.serviceArea ? 'serviceAreaError' : null
            "
            aria-haspopup="listbox"
            aria-labelledby="serviceAreaLabel"
            (click)="toggleServiceAreaDropdown($event)"
          >
            <span [class.text-brand-muted]="!selectedServiceAreaLabel">
              {{ selectedServiceAreaLabel || 'Please select' }}
            </span>
            <mat-icon class="comcast-select-icon" aria-hidden="true">
              {{
                isServiceAreaDropdownOpen()
                  ? 'arrow_drop_up'
                  : 'arrow_drop_down'
              }}
            </mat-icon>
          </button>

          @if (isServiceAreaDropdownOpen()) {
            <!-- a11y listbox pattern: docs/accessibility/custom-listbox.md -->
            <div
              class="comcast-select-panel"
              [class.comcast-select-panel--open]="isServiceAreaDropdownOpen()"
              [class.comcast-select-panel--invalid]="inlineErrors.serviceArea"
              role="listbox"
              aria-labelledby="serviceAreaLabel"
              (click)="$event.stopPropagation()"
            >
              <div class="comcast-select-options">
                @for (serviceArea of serviceAreaOptions; track serviceArea) {
                  <div
                    class="comcast-select-option"
                    [class.comcast-select-option--selected]="
                      nocFormGroup.value.serviceArea === serviceArea
                    "
                    role="option"
                    [attr.aria-selected]="
                      nocFormGroup.value.serviceArea === serviceArea
                    "
                    (click)="selectServiceArea(serviceArea, $event)"
                  >
                    {{ serviceArea }}
                  </div>
                }
              </div>
            </div>
          }
        </div>
        @if (inlineErrors.serviceArea) {
          <div
            id="serviceAreaError"
            class="comcast-validation-error"
            role="alert"
          >
            {{ inlineErrors.serviceArea }}
          </div>
        }
      </div>

      @if (
        nocFormGroup.value.serviceArea === 'My Service Is Down' ||
        nocFormGroup.value.serviceArea ===
          'My Service is Degraded or Intermittent'
      ) {
        <div class="mt-4 flex flex-wrap items-start gap-x-12 gap-y-4">
          <div>
            <label id="isPowerOnLabel" class="comcast-input-label">
              Power verified <span class="comcast-required-marker">*</span>
            </label>
            <div
              class="comcast-toggle-group grid-cols-2"
              [ngClass]="{ 'comcast-input-invalid': inlineErrors.isPowerOn }"
              role="radiogroup"
              aria-labelledby="isPowerOnLabel"
              [attr.aria-describedby]="
                inlineErrors.isPowerOn ? 'isPowerOnError' : null
              "
            >
              @for (option of powerVerificationOptions; track option.value) {
                <button
                  type="button"
                  class="comcast-toggle-btn min-w-20 border-r border-gray-400 last:border-r-0"
                  [class.comcast-toggle-btn--selected]="
                    nocFormGroup.value.isPowerOn === option.value
                  "
                  role="radio"
                  [attr.aria-checked]="
                    nocFormGroup.value.isPowerOn === option.value
                  "
                  (click)="selectPowerVerification(option.value)"
                >
                  {{ option.label }}
                </button>
              }
            </div>
            @if (inlineErrors.isPowerOn) {
              <div
                id="isPowerOnError"
                class="comcast-validation-error"
                role="alert"
              >
                {{ inlineErrors.isPowerOn }}
              </div>
            }
          </div>

          <div>
            <label id="intrusiveTestsLabel" class="comcast-input-label">
              Intrusive tests <span class="comcast-required-marker">*</span>
            </label>
            <div
              class="comcast-toggle-group grid-cols-3"
              [ngClass]="{
                'comcast-input-invalid': inlineErrors.canPerformIntrusiveTests,
              }"
              role="radiogroup"
              aria-labelledby="intrusiveTestsLabel"
              [attr.aria-describedby]="
                inlineErrors.canPerformIntrusiveTests
                  ? 'intrusiveTestsError'
                  : null
              "
            >
              @for (option of intrusiveTestingOptions; track option.value) {
                <button
                  type="button"
                  class="comcast-toggle-btn min-w-24 border-b border-gray-400 whitespace-nowrap last:border-b-0 sm:border-r sm:border-b-0 sm:last:border-r-0"
                  [class.comcast-toggle-btn--selected]="
                    nocFormGroup.value.canPerformIntrusiveTests === option.value
                  "
                  role="radio"
                  [attr.aria-checked]="
                    nocFormGroup.value.canPerformIntrusiveTests === option.value
                  "
                  (click)="selectIntrusiveTesting(option.value)"
                >
                  {{ option.label }}
                </button>
              }
            </div>
            @if (inlineErrors.canPerformIntrusiveTests) {
              <div
                id="intrusiveTestsError"
                class="comcast-validation-error"
                role="alert"
              >
                {{ inlineErrors.canPerformIntrusiveTests }}
              </div>
            }
          </div>
        </div>

        @if (nocFormGroup.value.canPerformIntrusiveTests === 'Yes (Custom)') {
          <div class="mt-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label for="testHoursStart" class="comcast-input-label">
                  From <span class="comcast-required-marker">*</span>
                </label>
                <input
                  id="testHoursStart"
                  class="comcast-input"
                  [ngClass]="{
                    'comcast-input-invalid': inlineErrors.testHoursStart,
                  }"
                  [attr.aria-describedby]="
                    inlineErrors.testHoursStart ? 'testHoursStartError' : null
                  "
                  type="time"
                  formControlName="testHoursStart"
                  (change)="validateControl('testHoursStart')"
                />
                @if (inlineErrors.testHoursStart) {
                  <div
                    id="testHoursStartError"
                    class="comcast-validation-error"
                    role="alert"
                  >
                    {{ inlineErrors.testHoursStart }}
                  </div>
                }
              </div>

              <div>
                <label for="testHoursEnd" class="comcast-input-label">
                  To <span class="comcast-required-marker">*</span>
                </label>
                <input
                  id="testHoursEnd"
                  class="comcast-input"
                  [ngClass]="{
                    'comcast-input-invalid': inlineErrors.testHoursEnd,
                  }"
                  [attr.aria-describedby]="
                    inlineErrors.testHoursEnd ? 'testHoursEndError' : null
                  "
                  type="time"
                  formControlName="testHoursEnd"
                  (change)="validateControl('testHoursEnd')"
                />
                @if (inlineErrors.testHoursEnd) {
                  <div
                    id="testHoursEndError"
                    class="comcast-validation-error"
                    role="alert"
                  >
                    {{ inlineErrors.testHoursEnd }}
                  </div>
                }
              </div>

              <div class="col-span-2">
                <label id="testDaysLabel" class="comcast-input-label">
                  Days <span class="comcast-required-marker">*</span>
                </label>
                <div
                  class="mt-2 grid grid-cols-2 gap-2 rounded-sm border border-blue-100 bg-gray-50 p-4 sm:grid-cols-3 md:grid-cols-4"
                  [ngClass]="{
                    'comcast-input-invalid': inlineErrors.testDays,
                  }"
                  role="group"
                  aria-labelledby="testDaysLabel"
                  [attr.aria-describedby]="
                    inlineErrors.testDays ? 'testDaysError' : null
                  "
                >
                  @for (day of testDayOptions; track day) {
                    <button
                      type="button"
                      class="comcast-day-btn"
                      [class.comcast-day-btn--selected]="isTestDaySelected(day)"
                      [attr.aria-pressed]="isTestDaySelected(day)"
                      (click)="toggleTestDay(day)"
                    >
                      {{ day }}
                    </button>
                  }
                </div>
                @if (inlineErrors.testDays) {
                  <div
                    id="testDaysError"
                    class="comcast-validation-error"
                    role="alert"
                  >
                    {{ inlineErrors.testDays }}
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <div class="mt-4">
          <label for="accessHours" class="comcast-input-label">
            Site access hours <span class="comcast-required-marker">*</span>
          </label>
          <input
            id="accessHours"
            class="comcast-input"
            [ngClass]="{ 'comcast-input-invalid': inlineErrors.accessHours }"
            [attr.aria-describedby]="
              inlineErrors.accessHours ? 'accessHoursError' : null
            "
            type="text"
            formControlName="accessHours"
            (keyup)="validateControl('accessHours')"
          />
          @if (inlineErrors.accessHours) {
            <div
              id="accessHoursError"
              class="comcast-validation-error"
              role="alert"
            >
              {{ inlineErrors.accessHours }}
            </div>
          }
        </div>

        <div class="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label for="siteLocalFirst" class="comcast-input-label">
              Site contact first name
              <span class="comcast-required-marker">*</span>
            </label>
            <input
              id="siteLocalFirst"
              class="comcast-input"
              [ngClass]="{
                'comcast-input-invalid': inlineErrors.siteLocalFirst,
              }"
              [attr.aria-describedby]="
                inlineErrors.siteLocalFirst ? 'siteLocalFirstError' : null
              "
              type="text"
              formControlName="siteLocalFirst"
              placeholder="First name"
              (keyup)="validateControl('siteLocalFirst')"
            />
            @if (inlineErrors.siteLocalFirst) {
              <div
                id="siteLocalFirstError"
                class="comcast-validation-error"
                role="alert"
              >
                {{ inlineErrors.siteLocalFirst }}
              </div>
            }
          </div>

          <div>
            <label for="siteLocalLast" class="comcast-input-label">
              Site contact last name
              <span class="comcast-required-marker">*</span>
            </label>
            <input
              id="siteLocalLast"
              class="comcast-input"
              [ngClass]="{
                'comcast-input-invalid': inlineErrors.siteLocalLast,
              }"
              [attr.aria-describedby]="
                inlineErrors.siteLocalLast ? 'siteLocalLastError' : null
              "
              type="text"
              formControlName="siteLocalLast"
              placeholder="Last name"
              (keyup)="validateControl('siteLocalLast')"
            />
            @if (inlineErrors.siteLocalLast) {
              <div
                id="siteLocalLastError"
                class="comcast-validation-error"
                role="alert"
              >
                {{ inlineErrors.siteLocalLast }}
              </div>
            }
          </div>

          <div>
            <label for="siteLocalEmail" class="comcast-input-label">
              Site contact email <span class="comcast-required-marker">*</span>
            </label>
            <input
              id="siteLocalEmail"
              class="comcast-input"
              type="email"
              formControlName="siteLocalEmail"
              placeholder="Email"
              (keyup)="validateControl('siteLocalEmail')"
              [ngClass]="{
                'comcast-input-invalid': inlineErrors.siteLocalEmail,
              }"
              [attr.aria-invalid]="!!inlineErrors.siteLocalEmail"
              [attr.aria-describedby]="
                inlineErrors.siteLocalEmail ? 'siteLocalEmailError' : null
              "
            />
            @if (inlineErrors.siteLocalEmail) {
              <div
                id="siteLocalEmailError"
                class="comcast-validation-error"
                role="alert"
              >
                {{ inlineErrors.siteLocalEmail }}
              </div>
            }
          </div>

          <div>
            <label for="siteLocalPhone" class="comcast-input-label">
              Site contact phone <span class="comcast-required-marker">*</span>
            </label>
            <input
              id="siteLocalPhone"
              class="comcast-input"
              [ngClass]="{
                'comcast-input-invalid': inlineErrors.siteLocalPhone,
              }"
              [attr.aria-describedby]="
                inlineErrors.siteLocalPhone ? 'siteLocalPhoneError' : null
              "
              type="tel"
              formControlName="siteLocalPhone"
              placeholder="Phone"
              (keyup)="validateControl('siteLocalPhone')"
            />
            @if (inlineErrors.siteLocalPhone) {
              <div
                id="siteLocalPhoneError"
                class="comcast-validation-error"
                role="alert"
              >
                {{ inlineErrors.siteLocalPhone }}
              </div>
            }
          </div>
        </div>
      }
      <div class="mt-4">
        <label for="nocDescription" class="comcast-input-label">
          Please describe the issue(s) you're experiencing.
          <span class="comcast-required-marker">*</span>
        </label>
        <textarea
          id="nocDescription"
          formControlName="description"
          class="comcast-textarea"
          [ngClass]="{ 'comcast-input-invalid': inlineErrors.description }"
          [attr.aria-describedby]="
            inlineErrors.description ? 'descriptionError' : 'descriptionCount'
          "
          rows="5"
          maxlength="400"
          placeholder="Tell us about the issue..."
          (keyup)="validateControl('description')"
        ></textarea>
        <div class="mt-1 flex items-center justify-between">
          @if (inlineErrors.description) {
            <div
              id="descriptionError"
              class="comcast-validation-error !mt-0"
              role="alert"
            >
              {{ inlineErrors.description }}
            </div>
          } @else {
            <div></div>
          }
          <div
            id="descriptionCount"
            class="comcast-textarea-count !mt-0"
            aria-live="polite"
            aria-atomic="true"
          >
            {{ nocFormGroup.get('description').value?.length || 0 }} / 400
          </div>
        </div>
      </div>
      @if (!hideCustomerTicketNumber) {
        <div class="mt-4">
          <label for="customerTicketNumber" class="comcast-input-label">
            Customer Ticket Number
          </label>
          <input
            id="customerTicketNumber"
            type="text"
            class="comcast-input"
            formControlName="customerTicketNumber"
            aria-label="Customer ticket number"
          />
        </div>
      }

      @if (submissionError) {
        <div class="comcast-error-alert mt-4" role="alert">
          <mat-icon
            fontSet="material-icons-outlined"
            class="comcast-error-alert-icon"
            aria-hidden="true"
            >warning</mat-icon
          >
          <span class="comcast-error-alert-text">
            {{
              errorMessage || 'Unable to submit the ticket. Please try again!'
            }}
          </span>
        </div>
      }

      <div class="mt-6">
        <button
          type="button"
          class="btn-primary w-full"
          (click)="submitTicket()"
          [disabled]="areServicesLoading() || areUsersLoading() || isSubmitting"
          [attr.aria-label]="
            isSubmitting ? 'Submitting ticket, please wait' : null
          "
          [attr.aria-busy]="isSubmitting || null"
        >
          @if (isSubmitting) {
            <mat-spinner
              [diameter]="20"
              color="accent"
              aria-hidden="true"
            ></mat-spinner>
          } @else {
            Submit ticket
          }
        </button>
      </div>
    </form>
  }
</section>
